import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/EmailHistory.css"; // reuses .status-badge and its variant classes
import { summarizeStatus } from "./communicationStatus";
import EmailDetailsModal from "./EmailDetailsModal";

// Shows every email ever sent to one specific person, right on their own participant
// page — same status badges and detail modal as the global Communication History
// table (EmailHistory.js), just scoped to one personId and unpaginated (per-person
// volume is naturally small).
const ParticipantEmailHistory = ({ personId }) => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusById, setStatusById] = useState({});
  const [selectedRecord, setSelectedRecord] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!personId) return;
    setIsLoading(true);
    axios
      .get(`${API_URL}/ems/communication-history/person/${personId}`)
      .then((res) => setHistory(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Failed to fetch this person's email history", err))
      .finally(() => setIsLoading(false));
  }, [personId, API_URL]);

  // Same StrictMode-safe batch status fetch as EmailHistory.js.
  useEffect(() => {
    if (history.length === 0) return;
    const controller = new AbortController();
    const ids = history.map((item) => item._id);
    axios
      .post(`${API_URL}/ems/communication-history/status/batch`, { ids }, { signal: controller.signal })
      .then((res) => setStatusById(res.data || {}))
      .catch((err) => {
        if (axios.isCancel(err) || err.code === "ERR_CANCELED") return;
        console.error("Failed to fetch email statuses", err);
      });
    return () => controller.abort();
  }, [history, API_URL]);

  if (!personId) return null;

  return (
    <div className="mb-4">
      <h6 className="fw-bold mb-2">Email History</h6>
      {isLoading ? (
        <p className="text-muted">Loading...</p>
      ) : history.length === 0 ? (
        <p className="text-muted">No emails sent to this person yet.</p>
      ) : (
        <div className="list-group">
          {history.map((record) => {
            const summary = statusById[record._id]
              ? summarizeStatus(statusById[record._id].events, statusById[record._id].status)
              : null;
            return (
              <button
                type="button"
                key={record._id}
                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                onClick={() => setSelectedRecord(record)}
              >
                <span>
                  {summary ? (
                    <span className={`status-badge ${summary.className} me-2`}>{summary.badge}</span>
                  ) : (
                    <span className="status-badge status-badge--neutral me-2">...</span>
                  )}
                  {record.subject}
                </span>
                <span className="text-muted small">
                  {record.sent_at
                    ? new Date(record.sent_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                    : "-"}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {selectedRecord && (
        <EmailDetailsModal
          record={selectedRecord}
          statusSummary={
            statusById[selectedRecord._id]
              ? summarizeStatus(statusById[selectedRecord._id].events, statusById[selectedRecord._id].status)
              : null
          }
          onClose={() => setSelectedRecord(null)}
        />
      )}
    </div>
  );
};

export default ParticipantEmailHistory;
